export type LlmEntry = {
  id: string;
  label: string;
  provider?: string;
};

export type MagiMethod =
  | "VoteYesNo"
  | "VoteOptions"
  | "Majority"
  | "Consensus"
  | "Minority"
  | "Probability"
  | "Compose"
  | "Synthesis";

export const LANGUAGE_SUGGESTIONS_DEFAULT = [
  "English",
  "Japanese (日本語)",
  "Traditional Chinese (繁體中文)",
  "Simplified Chinese (简体中文)",
  "German (Deutsch)",
  "Spanish (Español)",
  "French (Français)",
  "Portuguese (Português)",
];

export type AppConfig = {
  serviceBaseUrl: string;
  serviceToken: string;
  defaults: {
    deliberative: boolean;
    voteThreshold: number;
    allowAbstain: boolean;
    theme: "dark" | "light";
    outputFormat: "structured" | "markdown";
  };
  suggestedLanguages: string[];
};

const FALLBACK: AppConfig = {
  serviceBaseUrl: "http://localhost:8000",
  serviceToken: "",
  defaults: {
    deliberative: true,
    voteThreshold: 0.5,
    allowAbstain: true,
    theme: "light",
    outputFormat: "structured",
  },
  suggestedLanguages: LANGUAGE_SUGGESTIONS_DEFAULT,
};

export async function loadConfig(): Promise<AppConfig> {
  let runtime: Partial<AppConfig> = {};
  try {
    const resp = await fetch(
      `${import.meta.env.BASE_URL || "/"}config.json?_=${Date.now()}`,
      { cache: "no-store" },
    );
    if (resp.ok) runtime = (await resp.json()) as Partial<AppConfig>;
  } catch {
    // config.json is optional — build-time env vars may supply values instead.
  }

  const env = (import.meta.env ?? {}) as Record<string, string | undefined>;
  const merged: AppConfig = {
    serviceBaseUrl:
      runtime.serviceBaseUrl ||
      env.VITE_MAGI_SERVICE_URL ||
      FALLBACK.serviceBaseUrl,
    serviceToken:
      runtime.serviceToken || env.VITE_MAGI_SERVICE_TOKEN || FALLBACK.serviceToken,
    defaults: { ...FALLBACK.defaults, ...(runtime.defaults || {}) },
    suggestedLanguages:
      runtime.suggestedLanguages && runtime.suggestedLanguages.length > 0
        ? runtime.suggestedLanguages
        : FALLBACK.suggestedLanguages,
  };

  merged.serviceBaseUrl = merged.serviceBaseUrl.replace(/\/+$/, "");

  if (merged.serviceToken && !merged.serviceToken.startsWith("magi_")) {
    // eslint-disable-next-line no-console
    console.warn(
      '[magi] serviceToken does not start with "magi_" — the MAGI-Service will reject it.',
    );
  }

  return merged;
}

export const _internal = { FALLBACK };
